const importingRulepacksContent = {
  title: "Importing Rulepacks",
  description: "Import ready-made automation rules from the community Discover marketplace, then adapt them to your folders.",
  sections: [
    {
      id: "overview",
      title: "What Rulepacks Are",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          A <strong>rulepack</strong> is a bundle of prebuilt <a href="/docs/watch-folders-automations" class="text-[#06c]">automations</a>
          published to Sortio's community <strong>Discover</strong> marketplace. Instead of building a set
          of rules from scratch (say, a full receipts workflow or a student study-materials setup), you
          import a pack and get every rule in it added to your Automations page in one click.
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          Each pack card shows the pack name, its author, a short description, how many rules it
          contains, and a community vote count so you can see what other users have found useful.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] mb-2">Where to find it</h4>
          <p class="text-[#424245] text-sm">Open the <strong>Discover</strong> page from the app's sidebar. You can search packs by name, description, or author, and upvote packs you like.</p>
        </div>
      `
    },
    {
      id: "importing",
      title: "Importing a Pack",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-5">
            <h4 class="font-semibold text-[#1d1d1f]">Step-by-step</h4>
          </div>
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">1</span>
                </div>
                Open Discover and find a pack
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Browse or search. The rule count badge tells you how many automations the pack will add.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">2</span>
                </div>
                Click Import
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Sortio downloads the pack and confirms with a message like "Imported Student Study Materials: 6 rules added."</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0">
                  <span class="text-white font-medium text-sm">3</span>
                </div>
                Find the rules in Automations
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Every rule from the pack appears on your Automations page with its conditions and actions intact, ready to inspect, edit, run, or assign to a Space.</p>
            </div>
          </div>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">ℹ️</span>
            Use v8.17.0 or newer
          </h4>
          <p class="text-[#424245] text-sm">Rulepack import was overhauled end to end in Sortio v8.17.0. If imports fail or imported rules show up empty, update the app first (Sortio auto-updates, or grab the latest from the <a href="/download" class="text-[#06c]">download page</a>).</p>
        </div>
      `
    },
    {
      id: "after-import",
      title: "After Importing: Adapt, Test, Assign",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Imported rules are a starting point, not a finished setup. Pack authors built them around their
          own folder layout, so spend two minutes adapting them to yours.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Check the destination folders</h4>
              <p class="text-[#424245]">Open each rule and point its Move destinations at folders that exist on your machine. This is the most common edit an imported pack needs.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Dry Run before enabling</h4>
              <p class="text-[#424245]">Run each rule as a <strong>Dry Run</strong> to see exactly which of your files it would match and where they would go, with nothing changed on disk.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Assign to a Space if you want it ongoing</h4>
              <p class="text-[#424245]">To have imported rules keep a folder organized automatically, assign them to a <a href="/docs/spaces" class="text-[#06c]">Space</a> and set the Space to run automations when files are added. See <a href="/docs/watch-folders-automations" class="text-[#06c]">Watch Folders &amp; Automations</a>.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Prune what you don't need</h4>
              <p class="text-[#424245]">Packs often cover more cases than you have. Disable or delete the rules that don't apply; the rest of the pack keeps working.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "plans",
      title: "Plans and AI Usage",
      content: `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Free</h4>
            <p class="text-[#424245] text-sm">Browse the full Discover marketplace and vote on packs. Importing requires an upgrade.</p>
          </div>
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Pro / Team</h4>
            <p class="text-[#424245] text-sm">Unlimited rulepack imports.</p>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg">
          Importing a pack and running its rules doesn't use your AI allowance at all. Rule-based
          sorting is deterministic, free, and unlimited, however many rules a pack adds.
        </p>
      `
    }
  ]
};

export default importingRulepacksContent;
